import BaseInteraction from '../player/BaseInteraction';
import BitmovinInteraction from '../player/BitmovinInteraction';
import FlowplayerInteraction from '../player/FlowplayerInteraction';
import FlowplayerHLSjsInteraction from '../player/FlowplayerHLSjsInteraction';
import HLSjsInteraction from '../player/HLSjsInteraction';
import HLSjsConfigInteraction from '../player/HLSjsConfigInteraction';
import PlayerType from '../player/PlayerType';
import ShakaInteraction from '../player/ShakaInteraction';
import THEOplayerInteraction from '../player/THEOplayerInteraction';
import ViblastInteraction from '../player/ViblastInteraction';
import parseVersion from '../util/parseVersion';
import {
  ViblastOptions,
  ViblastVideoJsPlayer,
  XHRFactoryOptions
} from '@kollective-frontend/ksdk-api';
import VideoJsInteraction from '../player/VideoJsInteraction';
import {
  BitmovinOptions,
  FetchFactoryOptions,
  FlowplayerConstructor,
  FlowplayerOptions,
  HLSjsConfigWrapper
} from '@kollective-frontend/ksdk-api';
import {
  Flowplayer,
  HLS,
  ShakaPlayer,
  THEOPlayer,
  VideoJSPlayer,
  VideoJSOptions
} from '../player/interfaces';
import FetchFactoryPlayerInteraction from '../player/FetchFactoryPlayerInteraction';
import { TechApi } from '../bootloader/tech';
import XHRFactoryPlayerInteraction from '../player/XHRFactoryPlayerInteraction';

declare global {
  interface Window {
    flowplayer?: FlowplayerConstructor;
  }
}

export function plannedError(playerType: PlayerType): Error {
  return new Error(
    `${playerType} integration has yet to be implemented, but it's planned. Feel free to reach out to Kollective regarding this issue.`
  );
}

export function unPlannedError(playerType: PlayerType): Error {
  return new Error(
    `${playerType} has yet to be implemented, and it is not planned. Feel free to reach out to Kollective for any questions.`
  );
}

export function isFlowplayerV3(fplayer: unknown, options?: FlowplayerOptions) {
  const version = parseVersion(options?.version ?? window.flowplayer?.version);
  const player = fplayer as Flowplayer;
  return !player.opts?.hls || version.major >= 3;
}

export function handleFlowplayer(
  player: unknown,
  playerOptions?: FlowplayerOptions
) {
  const fPlayer = player as Flowplayer;
  if (isFlowplayerV3(player, playerOptions)) {
    const hls = playerOptions?.hls ?? window.flowplayer?.hls;
    const options = { strictness: 'lenient', version: hls?.version ?? '1' };
    return new FlowplayerHLSjsInteraction(fPlayer, options);
  }
  return new FlowplayerInteraction(fPlayer);
}

function getInteraction(
  // we use unknown because we do not own types for the passed in player,
  player: unknown,
  playerType: PlayerType,
  playerOptions: unknown,
  tech: TechApi
): BaseInteraction<unknown> {
  switch (playerType) {
    case PlayerType.AMP:
      throw plannedError(playerType);
    case PlayerType.Bitmovin:
      return new BitmovinInteraction(playerOptions as BitmovinOptions);
    case PlayerType.Flowplayer:
      return handleFlowplayer(player, playerOptions as FlowplayerOptions);
    case PlayerType.FetchFactoryPlayer:
      return new FetchFactoryPlayerInteraction(player as FetchFactoryOptions);
    case PlayerType.XHRFactoryPlayer:
      return new XHRFactoryPlayerInteraction(player as XHRFactoryOptions);
    case PlayerType.HLSjs:
      // in the case of HLS.js, the player is a video element, and the options is the hls js object
      return new HLSjsInteraction(
        player as HTMLVideoElement,
        playerOptions as HLS
      );
    case PlayerType.HLSjsConfig:
      return new HLSjsConfigInteraction(player as HLSjsConfigWrapper);
    case PlayerType.Shaka:
      return new ShakaInteraction(player as ShakaPlayer);
    case PlayerType.THEOplayer:
      return new THEOplayerInteraction(player as THEOPlayer);
    case PlayerType.Viblast:
      return new ViblastInteraction(
        player as ViblastVideoJsPlayer,
        playerOptions as ViblastOptions,
        tech
      );
    case PlayerType.VideoJS:
      return new VideoJsInteraction(
        player as VideoJSPlayer,
        playerOptions as VideoJSOptions
      );
    default:
      throw unPlannedError(playerType);
  }
}

export default function getPlayerInteraction(
  player: unknown,
  playerType: PlayerType,
  playerOptions: unknown,
  tech: TechApi
): BaseInteraction<unknown> {
  const interaction = getInteraction(player, playerType, playerOptions, tech);
  interaction.initialize();
  return interaction;
}
